import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const API_KEY = process.env.REACT_APP_ADMIN_API_KEY;

/**
 * Get WebSocket URL from environment variable
 * IMPORTANT: Always use this function instead of hardcoding URLs or using window.location
 * 
 * @returns {string} WebSocket URL from REACT_APP_WS_URL environment variable
 * 
 * @example
 * import { getWebSocketUrl } from '../services/api';
 * const ws = new WebSocket(getWebSocketUrl());
 */
export const getWebSocketUrl = () => {
  // Always use the dedicated WebSocket URL from environment variable
  // DO NOT use window.location or hardcode URLs
  const wsUrl = process.env.REACT_APP_WS_URL;
  
  if (!wsUrl) {
    console.error('[WebSocket] REACT_APP_WS_URL is not set in .env file!');
    // Fallback: derive from API URL (not recommended, but better than hardcoding)
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8001';
    let derivedWsUrl;
    
    if (apiUrl.startsWith('http://')) {
      derivedWsUrl = apiUrl.replace('http://', 'ws://');
    } else if (apiUrl.startsWith('https://')) {
      derivedWsUrl = apiUrl.replace('https://', 'wss://');
    } else if (apiUrl.startsWith('ws://') || apiUrl.startsWith('wss://')) {
      derivedWsUrl = apiUrl;
    } else {
      derivedWsUrl = `ws://${apiUrl.replace(/^https?:\/\//, '')}`;
    }
    
    console.warn('[WebSocket] Using fallback URL derived from API URL:', derivedWsUrl);
    return derivedWsUrl;
  }
  
  // Return the URL from .env as-is (no modifications)
  return wsUrl;
};

// Security: Only log error if API key is missing, never log the key itself
if (!API_KEY) {
  console.error('[API] REACT_APP_ADMIN_API_KEY is not set!');
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authentication to all requests using interceptor
api.interceptors.request.use(
  (config) => {
    // Try JWT token first (from localStorage)
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      // Fall back to API key if no token
      const apiKey = process.env.REACT_APP_ADMIN_API_KEY;
      if (apiKey) {
        config.headers['X-Admin-API-Key'] = apiKey;
      } else {
        console.error('[API] No token or API key available!');
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses (unauthorized) - redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_email');
      localStorage.removeItem('token_expires_in');
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboard = async () => {
  const response = await api.get('/admin/dashboard');
  return response.data;
};

// House info (network info)
export const getHouseInfo = async () => {
  const dashboard = await getDashboard();
  return {
    network: dashboard.is_testnet ? 'testnet' : 'mainnet',
    is_testnet: dashboard.is_testnet
  };
};

// Wallets
export const getAllWallets = async (includeBalance = true) => {
  const response = await api.get('/admin/wallets', {
    params: { include_balance: includeBalance }
  });
  return response.data;
};

export const generateWallet = async (walletData) => {
  // Wallet type is not used; backend auto-detects from address.
  // Expect object: { multiplier, address, private_key, chance, min_bet_btc?, max_bet_btc? }
  const response = await api.post('/admin/wallet/generate', walletData);
  return response.data;
};

export const updateWallet = async (walletId, updates) => {
  const response = await api.put(`/admin/wallet/${walletId}`, updates);
  return response.data;
};

export const deleteWallet = async (walletId) => {
  const response = await api.delete(`/admin/wallet/${walletId}`);
  return response.data;
};


// Authentication
export const login = async (email, password) => {
  const response = await api.post('/admin/auth/login', {
    email,
    password
  });
  return response.data;
};

export const verify2FA = async (email, code) => {
  const response = await api.post('/admin/auth/verify-2fa', {
    email,
    code
  });
  return response.data;
};

export const checkAuthStatus = async () => {
  const response = await api.get('/admin/auth/status');
  return response.data;
};

export const resend2FACode = async (email) => {
  const response = await api.post('/admin/auth/resend-code', {
    email
  });
  return response.data;
};

export const logout = async () => {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_email');
  localStorage.removeItem('token_expires_in');
  try {
    await api.post('/admin/auth/logout');
  } catch (error) {
    // Ignore errors on logout
  }
};

// Analytics
export const getStats = async (period = 'today') => {
  const response = await api.get(`/admin/stats/${period}`);
  return response.data;
};

export const getVolumeByMultiplier = async (period = 'all') => {
  const response = await api.get('/admin/analytics/volume', {
    params: { period }
  });
  return response.data;
};

export const getDailyStats = async (days = 30) => {
  const response = await api.get('/admin/analytics/daily', {
    params: { days }
  });
  return response.data;
};

// Server Seeds
export const getAllServerSeeds = async () => {
  const response = await api.get('/admin/server-seeds');
  return response.data;
};

export const createServerSeed = async (seedDate) => {
  const response = await api.post('/admin/server-seed/create', { seed_date: seedDate });
  return response.data;
};

export const deleteServerSeed = async (seedId) => {
  const response = await api.delete(`/admin/server-seed/${seedId}`);
  return response.data;
};

// Backend Management
export const getBackendStatus = async () => {
  const response = await api.get('/admin/backend/status');
  return response.data;
};

export const startBackend = async () => {
  const response = await api.post('/admin/backend/start');
  return response.data;
};

export const stopBackend = async () => {
  const response = await api.post('/admin/backend/stop');
  return response.data;
};

export const restartBackend = async () => {
  const response = await api.post('/admin/backend/restart');
  return response.data;
};

export const getBetStatus = async () => {
  const response = await api.get('/admin/backend/bet-status');
  return response.data;
};

export const toggleBet = async (enabled) => {
  const response = await api.post('/admin/backend/bet-toggle', { enabled });
  return response.data;
};

export const getBackendLogs = async (lines = 1000) => {
  const response = await api.get('/admin/backend/logs', {
    params: { lines }
  });
  return response.data;
};

export default api;
